import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CreateQuoteDialog } from "../customer/CustomerQuotes";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Star, MapPin, Briefcase, ArrowLeft, Phone, Clock, Euro, CalendarDays, Crown } from "lucide-react";
import { toast } from "sonner";

const MODE_LABEL = { domicilio: "A domicilio", presso: "In sede", online: "Online" };
const PRICE_LABEL = { orario: "Prezzo orario", fisso: "Prezzo fisso", preventivo: "Preventivo personalizzato", gratuita: "Consulenza gratuita" };
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function BookingDialog({ pid, name, open, onOpenChange, onLoginNeeded, user }) {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [start, setStart] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const fetchSlots = async (d) => {
    setDate(d); setStart(""); setSlots([]);
    if (!d) return;
    setLoading(true);
    try { const { data } = await api.get(`/professionals/${pid}/availability`, { params: { date: d } }); setSlots(data.slots || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const submit = async () => {
    if (!user) { onLoginNeeded(); return; }
    if (!date || !start) { toast.error("Scegli data e orario"); return; }
    try {
      await api.post(`/professionals/${pid}/bookings`, { date, start, note });
      toast.success("Prenotazione inviata! Il professionista la confermerà.");
      onOpenChange(false); setDate(""); setStart(""); setNote(""); setSlots([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141416] border-white/10 text-white">
        <DialogHeader><DialogTitle>Prenota un appuntamento con {name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><label className="text-sm text-zinc-300">Data</label>
            <Input data-testid="booking-date" type="date" min={today} value={date} onChange={(e) => fetchSlots(e.target.value)} className="bg-[#0A0A0C] border-white/10" /></div>
          {date && (
            <div className="space-y-1"><label className="text-sm text-zinc-300">Orari disponibili</label>
              {loading ? <div className="text-sm text-zinc-500">Caricamento…</div> :
                slots.length === 0 ? <div data-testid="no-slots" className="text-sm text-zinc-500">Nessuno slot disponibile in questa data.</div> :
                <div className="flex gap-2 flex-wrap">{slots.map((s) => <button key={s} data-testid={`slot-${s}`} onClick={() => setStart(s)} className={`text-sm rounded-lg px-3 py-1.5 border ${start === s ? "bg-[#00E676]/15 border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-300"}`}>{s}</button>)}</div>}
            </div>
          )}
          <Textarea data-testid="booking-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi una nota (opzionale)" className="bg-[#0A0A0C] border-white/10" />
          <Button data-testid="booking-submit" onClick={submit} disabled={!start} className="w-full bg-[#00E676] text-black font-semibold">Conferma prenotazione</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [reqMode, setReqMode] = useState("contatto");
  const [msg, setMsg] = useState("");
  const [date, setDate] = useState("");
  const [phone, setPhone] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const goLogin = () => { toast.error("Accedi per prenotare"); navigate("/login", { state: { from: `/professionisti/${id}` } }); };

  const load = async () => { try { const { data } = await api.get(`/public/professionals/${id}`); setP(data); } catch { toast.error("Professionista non trovato"); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const sendRequest = async () => {
    if (!user) { toast.error("Accedi per inviare una richiesta"); navigate("/login", { state: { from: `/professionisti/${id}` } }); return; }
    try {
      await api.post(`/professionals/${id}/requests`, { mode: reqMode, message: msg, preferred_date: date, phone });
      toast.success("Richiesta inviata! Il professionista ti contatterà.");
      setOpen(false); setMsg(""); setDate("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReview = async () => {
    try { await api.post(`/professionals/${id}/reviews`, { rating, comment }); toast.success("Recensione pubblicata"); setComment(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!p) return <div className="min-h-screen bg-[#0A0A0C] text-[#00E676] flex items-center justify-center">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-24">
      <header className="sticky top-0 z-10 bg-[#0A0A0C]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button data-testid="pro-detail-back" onClick={() => navigate("/professionisti")} className="text-zinc-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
          <span className="font-['Unbounded'] font-bold">Profilo</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        <div className="flex gap-4">
          <div className="h-24 w-24 shrink-0 rounded-2xl overflow-hidden bg-[#1C1C1F] border border-white/10 flex items-center justify-center">
            {p.photo ? <img src={p.photo} alt={p.name} className="h-full w-full object-cover" /> : <Briefcase className="h-8 w-8 text-zinc-500" />}
          </div>
          <div className="min-w-0 flex-1">
            <h1 data-testid="pro-name" className="font-['Unbounded'] font-extrabold text-xl">{p.name}</h1>
            <div className="text-sm text-[#00E676]">{(p.categories || []).join(" · ")}</div>
            <div className="flex items-center gap-3 mt-1.5 text-sm text-zinc-400 flex-wrap">
              {p.rating > 0 && <span className="flex items-center gap-0.5 text-amber-300"><Star className="h-4 w-4 fill-amber-300" /> {p.rating} ({p.reviews_count})</span>}
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {p.area_mode === "canavese" ? "Tutto il Canavese" : p.area_mode === "raggio" ? `${p.zone} · ${p.radius_km}km` : p.zone}</span>
            </div>
          </div>
        </div>

        {p.headline && <p className="text-zinc-200">{p.headline}</p>}
        {p.description && <p className="text-sm text-zinc-400 whitespace-pre-line">{p.description}</p>}

        <div className="flex gap-2 flex-wrap">
          {(p.service_modes || []).map((m) => <span key={m} className="text-xs rounded-full bg-white/10 px-3 py-1">{MODE_LABEL[m]}</span>)}
          {p.pricing_mode && <span className="text-xs rounded-full bg-[#00E676]/15 text-[#00E676] px-3 py-1 flex items-center gap-1"><Euro className="h-3 w-3" /> {PRICE_LABEL[p.pricing_mode]}{p.pricing_mode === "orario" && p.price_hourly ? ` · €${p.price_hourly}/h` : ""}{p.pricing_mode === "fisso" && p.price_fixed ? ` · €${p.price_fixed}` : ""}</span>}
        </div>

        {(p.skills || []).length > 0 && (
          <div><div className="text-sm font-semibold mb-1">Competenze</div><div className="flex gap-1.5 flex-wrap">{p.skills.map((s, i) => <span key={i} className="text-xs rounded-full bg-white/5 border border-white/10 px-2.5 py-1">{s}</span>)}</div></div>
        )}

        {(p.availability || []).length > 0 && (
          <div><div className="text-sm font-semibold mb-1 flex items-center gap-1"><Clock className="h-4 w-4" /> Disponibilità</div>
            <div className="space-y-1 text-sm text-zinc-400">
              {p.availability.map((a, i) => <div key={i} data-testid={`avail-${i}`}>{DAYS[a.day] || a.day}: {a.from}–{a.to}</div>)}
            </div>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="pro-contact-btn" className="w-full h-12 rounded-full bg-[#00E676] text-black font-semibold text-base"><Phone className="h-4 w-4 mr-2" /> Richiedi contatto o prenotazione</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141416] border-white/10 text-white">
            <DialogHeader><DialogTitle>Invia una richiesta</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-2">
                {["contatto", "prenotazione"].map((m) => (
                  <button key={m} data-testid={`reqmode-${m}`} onClick={() => setReqMode(m)} className={`flex-1 rounded-lg py-2 text-sm border ${reqMode === m ? "bg-[#00E676]/15 border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-300"}`}>{m === "contatto" ? "Contatto" : "Prenotazione"}</button>
                ))}
              </div>
              {reqMode === "prenotazione" && <Input data-testid="req-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#0A0A0C] border-white/10" />}
              <Input data-testid="req-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono (opzionale)" className="bg-[#0A0A0C] border-white/10" />
              <Textarea data-testid="req-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Descrivi ciò di cui hai bisogno…" className="bg-[#0A0A0C] border-white/10" />
              <Button data-testid="req-send" onClick={sendRequest} className="w-full bg-[#00E676] text-black font-semibold">Invia richiesta</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Button data-testid="pro-book-btn" onClick={() => { if (!user) { goLogin(); return; } setBookOpen(true); }} className="w-full h-12 rounded-full bg-white/10 hover:bg-white/20 font-semibold text-base"><CalendarDays className="h-4 w-4 mr-2" /> Prenota un appuntamento</Button>
        <BookingDialog pid={id} name={p.name} open={bookOpen} onOpenChange={setBookOpen} onLoginNeeded={goLogin} user={user} />

        <Button data-testid="pro-quote-btn" onClick={() => { if (!user) { toast.error("Accedi per richiedere un preventivo"); navigate("/login", { state: { from: `/professionisti/${id}` } }); return; } setQuoteOpen(true); }} variant="outline" className="w-full h-12 rounded-full border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 font-semibold text-base">Richiedi un preventivo</Button>
        <CreateQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} targetProfessional={p} onCreated={() => toast.success("Trovi la richiesta in 'I miei preventivi'")} />

        <div className="border-t border-white/10 pt-4">
          <div className="font-semibold mb-3">Recensioni ({p.reviews_count || 0})</div>
          {user && (
            <div className="bg-[#141416] border border-white/10 rounded-xl p-3 mb-4 space-y-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}><Star className={`h-6 w-6 ${n <= rating ? "fill-amber-300 text-amber-300" : "text-zinc-600"}`} /></button>)}
              </div>
              <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Com'è andata? (recensione disponibile dopo una richiesta)" className="bg-[#0A0A0C] border-white/10" />
              <Button data-testid="review-send" onClick={sendReview} size="sm" className="bg-[#00E676] text-black font-semibold">Pubblica recensione</Button>
            </div>
          )}
          <div className="space-y-3">
            {(p.reviews || []).map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="bg-[#141416] border border-white/10 rounded-xl p-3">
                <div className="flex items-center gap-2 text-sm"><span className="font-semibold">{r.user_name}</span><span className="flex items-center gap-0.5 text-amber-300"><Star className="h-3 w-3 fill-amber-300" /> {r.rating}</span></div>
                {r.comment && <p className="text-sm text-zinc-400 mt-1">{r.comment}</p>}
              </div>
            ))}
            {(p.reviews || []).length === 0 && <div className="text-sm text-zinc-500">Ancora nessuna recensione.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
